import { enableClassifierJudges } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { patchAIConfigVariationsBulk } from '@gonfalon/openapi/operations/patchAIConfigVariationsBulk';
import type { postAIConfigVariationsBulk } from '@gonfalon/openapi/operations/postAIConfigVariationsBulk';
import type { z } from 'zod';

import type { Tool } from '../AiLibrary/tools/types';
import type { AiConfigParametersType } from '../types';

import type {
  AiConfigMode,
  RegisteredSchemaUnion,
  RegisteredVariation,
  SchemaOfMode,
  VariationsFormSchema,
} from './registry';
import { type Classifier, classifiersForApi } from './schema/fields/classifiers';
import type { JudgeConfiguration } from './schema/fields/judges';
import { getEffectiveKey, getPayloadKeys, getVariationShape } from './utils';

type PostBulkBody = Parameters<typeof postAIConfigVariationsBulk>[0]['body'];
type PatchBulkBody = Parameters<typeof patchAIConfigVariationsBulk>[0]['body'];
type CreateVariationPayload = PostBulkBody['variations'][number];
type PatchVariationPayload = PatchBulkBody['patches'][number]['patch'];

/** Normalized model shape (modelName + optional parameters/custom/region). The other canonical shape is Record<string, never> for the opaque API model. */
export type ModelResponse = {
  modelName: string;
  parameters?: Record<string, unknown>;
  custom?: Record<string, unknown>;
  region?: string;
};

type VariationToolKey = { key: string; version: number };
type VariationSkillKey = { key: string; version: number };

export type ToolActions = {
  getToolsFromCache: (keys: Array<{ key: string; version: number }>) => Tool[];
  augmentParametersWithTools: (
    parameters: AiConfigParametersType | undefined,
    variationTools: Tool[] | undefined,
  ) => Record<string, unknown> | undefined;
};

export function asUnknownRecord<T extends ModelResponse>(model: T): Record<string, never> {
  return model as unknown as Record<string, never>;
}

/** Cast wide records to OpenAPI's `Record<string, never>` for JSON-valued fields (e.g. evaluation `parameters`). */
export function asNeverRecord(value: Record<string, unknown> | null | undefined): Record<string, never> {
  return (value ?? {}) as Record<string, never>;
}

export function asModelResponse(
  model: Record<string, never> | undefined | null,
  modelConfigKey?: string,
): ModelResponse | undefined {
  if (!model) {
    return undefined;
  }
  const apiModel = model as unknown as ModelResponse;
  return {
    modelName: apiModel.modelName || modelConfigKey || '',
    parameters: apiModel.parameters,
    custom: apiModel.custom,
    region: apiModel.region,
  };
}

export function parametersWithoutTools(
  params: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return undefined;
  }
  const { tools: _tools, ...rest } = params;
  return rest;
}

function generateUniqueKey(baseKey: string, existingKeys: Set<string>): string {
  if (!existingKeys.has(baseKey)) {
    return baseKey;
  }
  let counter = 1;
  let candidateKey = `${baseKey}-${counter}`;
  while (existingKeys.has(candidateKey)) {
    counter++;
    candidateKey = `${baseKey}-${counter}`;
  }
  return candidateKey;
}

function safeParseKey(
  keySchema: z.ZodTypeAny | undefined | null,
  value: unknown,
): { success: true; data: unknown } | { success: false } {
  if (!keySchema) {
    return { success: false };
  }
  const result = keySchema.safeParse(value);
  return result.success ? { success: true, data: result.data } : { success: false };
}

function normalizeModelForPayload(
  value: unknown,
  variation: RegisteredVariation,
  keySchema: z.ZodTypeAny | undefined,
  toolDeps: ToolActions | undefined,
  clearParameters = false,
): Record<string, never> | undefined {
  const parsed = safeParseKey(keySchema, value);
  if (!parsed.success) {
    return;
  }
  const modelValue = parsed.data as {
    modelName?: string;
    parameters?: Record<string, unknown>;
    custom?: Record<string, unknown>;
    region?: string;
  };
  const variationTools = 'tools' in variation ? (variation.tools ?? []) : [];
  const baseParameters = parametersWithoutTools(modelValue?.parameters) as AiConfigParametersType | undefined;
  const parameters =
    toolDeps?.augmentParametersWithTools(baseParameters, toolDeps?.getToolsFromCache(variationTools)) ??
    baseParameters ??
    {};
  const modelPayload: ModelResponse = {
    modelName: modelValue?.modelName ?? '',
    parameters,
    custom: modelValue?.custom ?? {},
  };
  if (modelValue?.region) {
    modelPayload.region = modelValue.region;
  }
  if (clearParameters) {
    modelPayload.parameters = {};
  }
  return asUnknownRecord(modelPayload);
}

function isClassifierList(value: unknown): value is Classifier[] {
  return (
    Array.isArray(value) &&
    value.every((item) => typeof item === 'object' && item !== null && 'key' in item && 'type' in item)
  );
}

export function normalizeToolsForApi(tools: VariationToolKey[] | undefined): VariationToolKey[] {
  if (tools === undefined) {
    return [];
  }
  return [...tools];
}

export function normalizeSkillsForApi(skills: VariationSkillKey[] | undefined): VariationSkillKey[] {
  if (skills === undefined) {
    return [];
  }
  return [...skills].sort((a, b) => a.key.localeCompare(b.key));
}

export function normalizeJudgeConfigurationForApi(
  value: JudgeConfiguration | unknown,
  keySchema?: z.ZodTypeAny,
): JudgeConfiguration | undefined {
  let data: JudgeConfiguration | unknown = value;
  if (keySchema) {
    const parsed = safeParseKey(keySchema, value);
    if (parsed.success) {
      data = parsed.data;
    }
  }
  const judgeConfiguration = data as JudgeConfiguration | undefined;
  if (judgeConfiguration === undefined || judgeConfiguration.judges === undefined) {
    return undefined;
  }
  return {
    judges: judgeConfiguration.judges.map((judge) => ({
      judgeConfigKey: judge.judgeConfigKey,
      samplingRate: judge.samplingRate,
    })),
  };
}

function classifierSetup(variation: RegisteredVariation): 'messages' | 'classifiers' | undefined {
  if ('setup' in variation && (variation.setup === 'messages' || variation.setup === 'classifiers')) {
    return variation.setup;
  }
  return undefined;
}

function isClassifierPayload(variation: RegisteredVariation): boolean {
  return enableClassifierJudges() && classifierSetup(variation) === 'classifiers';
}

function keepsStoredClassifiers(variation: RegisteredVariation): boolean {
  return 'storedClassifiersHidden' in variation && variation.storedClassifiersHidden === true;
}

function withClassifiers<T extends CreateVariationPayload | PatchVariationPayload>(
  acc: T,
  variation: RegisteredVariation,
  value: unknown,
): T {
  if (!enableClassifierJudges()) {
    return acc;
  }
  if (keepsStoredClassifiers(variation) && !isClassifierPayload(variation)) {
    return acc;
  }
  if (isClassifierPayload(variation)) {
    const classifiers = isClassifierList(value) ? classifiersForApi(value) : [];
    return { ...acc, classifiers };
  }
  return { ...acc, classifiers: [] };
}

function parseComment(data: Pick<RegisteredSchemaUnion, 'comment' | 'reason' | 'submitAction'>): string | undefined {
  switch (data.submitAction) {
    case 'approve':
      const trimmedReason = data.reason?.trim();
      return trimmedReason ? trimmedReason : undefined;
    case 'save':
      const trimmedComment = data.comment?.trim();
      return trimmedComment ? trimmedComment : undefined;
    default:
      return undefined;
  }
}

export function parseCreatePayload<M extends AiConfigMode>(
  schema: VariationsFormSchema,
  data: SchemaOfMode<M>,
  toolDeps?: ToolActions,
): PostBulkBody {
  const comment = parseComment(data);

  const existingKeys = new Set<string>(
    data.variations
      .filter((v: RegisteredVariation) => v.createdAt !== undefined)
      .map((v: RegisteredVariation) => v.key),
  );

  const variationShape = getVariationShape(schema);

  const variations = data.variations
    .filter((v: RegisteredVariation) => v.createdAt === undefined)
    .map((variation: RegisteredVariation) => {
      const baseKey = getEffectiveKey(variation);
      const uniqueKey = generateUniqueKey(baseKey, existingKeys);
      existingKeys.add(uniqueKey);

      const payload = getPayloadKeys(schema).reduce<CreateVariationPayload>((acc, key) => {
        const value = variation[key as keyof RegisteredVariation];
        const keySchema = variationShape[key];

        switch (key) {
          case 'key':
            return { ...acc, key: uniqueKey };
          case 'setup':
            return acc;
          case 'classifiers':
            return withClassifiers(acc, variation, value);
          case 'messages':
            if (keepsStoredClassifiers(variation)) {
              return acc;
            }
            if (isClassifierPayload(variation)) {
              return { ...acc, messages: [] };
            }
            if ('messages' in variation && variation.messages !== undefined) {
              return { ...acc, messages: variation.messages };
            }
            return acc;
          case 'model': {
            const normalized = normalizeModelForPayload(
              value,
              variation,
              keySchema,
              toolDeps,
              isClassifierPayload(variation),
            );
            if (normalized === undefined) {
              return acc;
            }
            return { ...acc, model: normalized };
          }
          case 'judgeConfiguration': {
            const normalized = normalizeJudgeConfigurationForApi(value, keySchema);
            if (normalized !== undefined) {
              return { ...acc, judgeConfiguration: normalized };
            }
            return acc;
          }
          case 'tools': {
            const parsed = safeParseKey(keySchema, value);
            if (parsed.success) {
              const toolsData = parsed.data as VariationToolKey[] | undefined;
              if (toolsData !== undefined) {
                return { ...acc, tools: normalizeToolsForApi(toolsData) };
              }
            }
            return acc;
          }
          case 'skills': {
            const parsed = safeParseKey(keySchema, value);
            if (parsed.success) {
              const skillsData = parsed.data as VariationSkillKey[] | undefined;
              if (skillsData !== undefined) {
                return { ...acc, skills: normalizeSkillsForApi(skillsData) };
              }
            }
            return acc;
          }
          default:
            if (value !== undefined) {
              return { ...acc, [key]: value } as CreateVariationPayload;
            }
            return acc;
        }
      }, {} as CreateVariationPayload);

      if (comment) {
        payload.comment = comment;
      }

      return payload;
    });

  return { variations };
}

export function parsePatchPayload<M extends AiConfigMode>(
  schema: VariationsFormSchema,
  data: SchemaOfMode<M>,
  toolDeps?: ToolActions,
): PatchBulkBody {
  const comment = parseComment(data);

  const variationShape = getVariationShape(schema);

  const patches = data.variations
    .filter((v: RegisteredVariation) => v.createdAt !== undefined)
    .map((variation: RegisteredVariation) => {
      const variationKey = variation.key;

      const patch = getPayloadKeys(schema).reduce<PatchVariationPayload>((acc, key) => {
        const value = variation[key as keyof RegisteredVariation];
        const keySchema = variationShape[key];

        switch (key) {
          case 'key':
            return acc;
          case 'setup':
            return acc;
          case 'classifiers':
            return withClassifiers(acc, variation, value);
          case 'messages':
            if (keepsStoredClassifiers(variation)) {
              return acc;
            }
            if (isClassifierPayload(variation)) {
              return { ...acc, messages: [] };
            }
            if ('messages' in variation && variation.messages !== undefined) {
              return { ...acc, messages: variation.messages };
            }
            return acc;
          case 'model': {
            const normalized = normalizeModelForPayload(
              value,
              variation,
              keySchema,
              toolDeps,
              isClassifierPayload(variation),
            );
            if (normalized === undefined) {
              return acc;
            }
            return { ...acc, model: normalized };
          }
          case 'judgeConfiguration': {
            const normalized = normalizeJudgeConfigurationForApi(value, keySchema);
            if (normalized !== undefined) {
              return { ...acc, judgeConfiguration: normalized };
            }
            return acc;
          }
          case 'tools': {
            const parsed = safeParseKey(keySchema, value);
            if (parsed.success) {
              const toolsData = parsed.data as VariationToolKey[] | undefined;
              if (toolsData !== undefined) {
                return { ...acc, tools: normalizeToolsForApi(toolsData) };
              }
            }
            return acc;
          }
          case 'skills': {
            const parsed = safeParseKey(keySchema, value);
            if (parsed.success) {
              const skillsData = parsed.data as VariationSkillKey[] | undefined;
              if (skillsData !== undefined) {
                return { ...acc, skills: normalizeSkillsForApi(skillsData) };
              }
            }
            return acc;
          }
          default:
            if (value !== undefined) {
              return { ...acc, [key]: value } as PatchVariationPayload;
            }
            return acc;
        }
      }, {} as PatchVariationPayload);

      if (comment) {
        patch.comment = comment;
      }

      return { variationKey, patch };
    });

  return { patches };
}
